import { Badge, Heading, Text } from '@astryxdesign/core';
import { Link as RouterLink } from 'react-router';

import { BLOG_POSTS_BY_DATE, formatPostDate, postPath, postShareImage, type BlogPost } from '../blog/posts';

/**
 * The blog hub.
 *
 * The newest article gets the wide card with its share image; the rest sit in
 * a grid below it. No tag cloud, no author widgets, no pagination for a
 * handful of posts. What it exists to do is give every article one internal
 * link from an indexed page and give a reader arriving from a search
 * somewhere to go next.
 */
export function BlogPage() {
  const [featured, ...rest] = BLOG_POSTS_BY_DATE;

  return (
    <div className="shell section-block">
      <div className="blog-head">
        <span className="eyebrow">Blog</span>
        <Heading level={1} className="display--md">
          Forecasting, <span className="gradient-text">benchmarked.</span>
        </Heading>
        <p className="lede measure--wide">
          Method notes, benchmarks and worked examples. Every number here is one we ran ourselves and
          published the raw output for, including the runs that went against us — a benchmark that
          only shows wins is not worth reading.
        </p>
      </div>

      {featured ? <FeaturedPost post={featured} /> : null}

      {rest.length > 0 ? (
        <div className="post-grid" style={{ marginTop: 18 }}>
          {rest.map((post) => (
            <PostCard key={post.slug} post={post} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Meta({ post }: { post: BlogPost }) {
  return (
    <div className="post-meta">
      <span className="kicker">{post.kicker}</span>
      <span>{formatPostDate(post.published)}</span>
      <span>·</span>
      <span>{post.readingMinutes} min read</span>
      {post.updated ? <Badge label={`Updated ${formatPostDate(post.updated)}`} variant="neutral" /> : null}
    </div>
  );
}

function FeaturedPost({ post }: { post: BlogPost }) {
  return (
    <RouterLink to={postPath(post.slug)} className="post-featured lift" aria-label={post.headline}>
      <div className="post-featured__cover">
        <img src={postShareImage(post)} alt="" width={1200} height={630} loading="eager" />
      </div>
      <div className="post-featured__body">
        <Meta post={post} />
        <h2 className="post-featured__title">{post.headline}</h2>
        <Text color="secondary">{post.description}</Text>
        <span className="post-card__more">Read the article →</span>
      </div>
    </RouterLink>
  );
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <RouterLink to={postPath(post.slug)} className="post-card lift" aria-label={post.headline}>
      <div className="post-card__cover">
        <img src={postShareImage(post)} alt="" width={1200} height={630} loading="lazy" />
      </div>
      <div className="post-card__body">
        <Meta post={post} />
        <h2 className="post-card__title">{post.headline}</h2>
        <Text size="sm" color="secondary">
          {post.description}
        </Text>
        <span className="post-card__more">Read →</span>
      </div>
    </RouterLink>
  );
}
